import { AlertOutlined, CarryOutOutlined, CheckSquareTwoTone, CloseSquareOutlined, KeyOutlined, SortAscendingOutlined, WarningTwoTone } from "@ant-design/icons";
import { Flex, FormInstance, FormListFieldData } from "antd";
import Tooltip from "components/atoms/Tooltip";
import ClassifiersTagField from "components/organisms/SchemaModelFieldList/ClassifiersTagField";
import React, { useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataTable from "../DataTable";

interface ISchemaModelFieldListReadOnlyProps {
    formRef: React.RefObject<FormInstance<ISchemaModel>>;
    fields: FormListFieldData[];
    onFieldRowSelected?: (r: FormListFieldData) => void;
    getFieldRowLevelError?: (i: number) => string | number | null;
}

export default function SchemaModelFieldListReadOnly({ formRef, fields, onFieldRowSelected, getFieldRowLevelError }: ISchemaModelFieldListReadOnlyProps) {
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

    const onClickRow = (record: FormListFieldData) => {
        return {
            onClick: () => {
                setSelectedRowKeys([record.key]);
                onFieldRowSelected && onFieldRowSelected(record);
            },
        };
    };

    return (
        <DataTable<FormListFieldData>
            dataTestId={CypressTestIds.SCHEMA_FIELD_TABLE}
            hideSelectionColumn
            fixedHeader
            tableProps={{
                size: "small",
                dataSource: fields,
                pagination: false,
                onRow: onClickRow,
                rowSelection: {
                    selectedRowKeys,
                },
                rowClassName: (field, index) => {
                    if (getFieldRowLevelError) {
                        let fieldError = getFieldRowLevelError(index);
                        if (fieldError) {
                            return "error-row";
                        }
                    }
                    return "";
                },
                className: "schema-model-field-table",
                columns: [
                    {
                        width: "30px",
                        title: "No.",
                        key: "index",
                        className: "align-top",
                        render: (value, field, index) => {
                            if (getFieldRowLevelError) {
                                const fieldError = getFieldRowLevelError(index);
                                return (
                                    <div className="flex gap-1">
                                        {fieldError ? (
                                            <Tooltip
                                                placement="topLeft"
                                                title={Object.values(fieldError).map((error, i) => (
                                                    <p key={`error-${i}`}>{error}</p>
                                                ))}
                                            >
                                                <WarningTwoTone twoToneColor="#FF0000" />
                                            </Tooltip>
                                        ) : null}
                                        {index + 1}
                                    </div>
                                );
                            }
                            return index + 1;
                        },
                    },
                    {
                        width: "60px",
                        dataIndex: "isPrimaryKey",
                        title: (
                            <Tooltip title="Is Primary Key?">
                                <Flex vertical className="items-center">
                                    <KeyOutlined alt="Is Primary Key?" />
                                    <span className="text-[7px]">Primary Key</span>
                                </Flex>
                            </Tooltip>
                        ),
                        ellipsis: {
                            showTitle: false,
                        },
                        align: "center",
                        className: "align-top",
                        render: (value, field, index) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "isPrimaryKey"]) ? (
                                <CheckSquareTwoTone />
                            ) : (
                                <CloseSquareOutlined className="text-gray-300" />
                            );
                        },
                    },
                    {
                        width: "60px",
                        dataIndex: "isSortKey",
                        title: (
                            <Tooltip title="Is Sort Key?">
                                <Flex vertical className="items-center">
                                    <SortAscendingOutlined alt="Is Sort Key?" />
                                    <span className="text-[7px]">Sort Key</span>
                                </Flex>
                            </Tooltip>
                        ),
                        ellipsis: {
                            showTitle: false,
                        },
                        align: "center",
                        className: "align-top",
                        render: (value, field, index) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "isSortKey"]) ? <CheckSquareTwoTone /> : <CloseSquareOutlined className="text-gray-300" />;
                        },
                    },
                    {
                        width: "60px",
                        dataIndex: "active",
                        title: (
                            <Tooltip title="Is Active?">
                                <Flex vertical className="items-center">
                                    <CarryOutOutlined alt="Is Active?" />
                                    <span className="text-[7px]">Active</span>
                                </Flex>
                            </Tooltip>
                        ),
                        ellipsis: {
                            showTitle: false,
                        },
                        align: "center",
                        className: "align-top",
                        render: (value, field, index) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "active"]) ? <CheckSquareTwoTone /> : <CloseSquareOutlined className="text-gray-300" />;
                        },
                    },
                    {
                        width: "60px",
                        dataIndex: "isRequired",
                        title: (
                            <Tooltip title="Is Required?">
                                <Flex vertical className="items-center">
                                    <AlertOutlined alt="Is Required?" />
                                    <span className="text-[7px]">Required</span>
                                </Flex>
                            </Tooltip>
                        ),
                        ellipsis: {
                            showTitle: false,
                        },
                        align: "center",
                        className: "align-top",
                        render: (value, field, index) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "isRequired"]) ? (
                                <CheckSquareTwoTone />
                            ) : (
                                <CloseSquareOutlined className="text-gray-300" />
                            );
                        },
                    },
                    {
                        width: "200px",
                        dataIndex: "name",
                        title: "Field Name",
                        ellipsis: {
                            showTitle: false,
                        },
                        className: "align-top",
                        render: (value, field) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "name"]);
                        },
                    },
                    {
                        width: "200px",
                        dataIndex: "description",
                        title: "Field Description",
                        ellipsis: {
                            showTitle: false,
                        },
                        className: "align-top",
                        render: (value, field) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "description"]);
                        },
                    },
                    {
                        width: "200px",
                        dataIndex: "dataType",
                        title: "Data Type",
                        ellipsis: {
                            showTitle: false,
                        },
                        className: "align-top",
                        render: (value, field) => {
                            return formRef.current?.getFieldValue(["fields", field.name, "dataType"]);
                        },
                    },
                    {
                        width: "150px",
                        dataIndex: "classifiers",
                        title: "Classifiers Tags",
                        ellipsis: {
                            showTitle: false,
                        },
                        className: "align-top",
                        render: (value, field, index) => {
                            const classifiers = formRef.current?.getFieldValue(["fields", field.name, "classifiers"]);
                            return classifiers ? (
                                <ClassifiersTagField fieldId={formRef.current?.getFieldValue(["fields", field.name, "fieldId"])} value={classifiers}></ClassifiersTagField>
                            ) : (
                                <></>
                            );
                        },
                    },
                ],
            }}
        />
    );
}
